import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./motion";
import { InViewTextEffect } from "@/components/ui/text-effect";

const LOGS = [
  {
    q: "Do I actually own my website?",
    a: "Yes, fully. Your domain, hosting, and files are registered in your name from day one. No lock-in, no hostage situation — it's your property, not a rental.",
  },
  {
    q: "Are there hidden fees?",
    a: "No. You pay the tier price, plus optional maintenance if you want it. Extra revision rounds are $150 each and always opt-in — nothing gets billed without your say-so.",
  },
  {
    q: "What happens if I want to leave?",
    a: "You get a full handoff of your domain, hosting, and files — no drama, no exit fees. Take it to any developer or host you like. I'll even help with the transfer.",
  },
  {
    q: "Will I be able to see what's going on?",
    a: "No black box. You get a live walkthrough at launch plus a control-boundary map showing exactly what you can change yourself and what stays locked down.",
  },
  {
    q: "What about SEO?",
    a: "Every tier ships with clean structure, fast load times, mobile-first layout, and basic on-page SEO. You start visible — no bolt-on 'SEO package' upsell required.",
  },
  {
    q: "How many revisions do I get?",
    a: "Two full rounds are included in every build. Need more? Additional rounds are $150 each, and you decide if and when to use them.",
  },
  {
    q: "Do you work with my industry?",
    a: "If you're an existing business with real customers — trades, shops, services — yes. I build for established local operators, not unproven startups or ideas.",
  },
  {
    q: "How fast do you respond?",
    a: "Same-day during your build. Since I only take one client a month, there's no queue and no ticket system — you get direct, prompt answers from the person building your site.",
  },
];

function LogEntry({ index, item, open, onToggle }) {
  const code = `LOG.${String(index + 1).padStart(2, "0")}`;
  return (
    <div className="border-b border-hairline" data-testid={`faq-entry-${index + 1}`}>
      <button
        onClick={onToggle}
        data-testid={`faq-trigger-${index + 1}`}
        className="flex w-full items-center gap-5 py-6 text-left"
        aria-expanded={open}
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-rust">
          {code}
        </span>
        <span className="flex-1 font-serif text-xl text-ink md:text-2xl">{item.q}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="flex-none font-mono text-2xl leading-none text-ink-dim"
        >
          +
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="max-w-2xl pb-7 pl-[4.6rem] text-sm leading-relaxed text-ink-dim md:text-base">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      className="relative z-10 border-t border-hairline bg-panel/40 px-5 py-24 md:px-8 md:py-32"
      data-testid="faq-section"
    >
      <div className="mx-auto max-w-4xl">
        <InViewTextEffect
          as="p"
          preset="blur"
          className="font-mono text-[11px] uppercase tracking-[0.24em] text-rust"
        >
          CH.06 // Diagnostic Log
        </InViewTextEffect>
        <Reveal
          as="h2"
          delay={0.05}
          className="mt-5 font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl"
        >
          Answered before you <em className="italic text-pine">ask</em>.
        </Reveal>

        <Reveal delay={0.1} className="mt-12 border-t border-hairline">
          {LOGS.map((item, i) => (
            <LogEntry
              key={i}
              index={i}
              item={item}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </Reveal>
      </div>
    </section>
  );
}
